import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { getFullName } from "@/lib/userUtils";
import { resolvePresence } from "@/lib/presence";
import { markdownToPlain, plainExcerpt } from "@/lib/markdownPlain";
import { permalinkForPost } from "@/lib/permalinks";
import { teamReferenceRoute } from "@/lib/contentReferences";
import { API_BASE_URL } from "@/lib/apiBase";
import { cn } from "@/lib/utils";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgFilter } from "@/scripts/OrgFilterContext";
import { useOptionalDmSocket } from "@/scripts/DmSocketContext";
import { useOptionalDmDrawer } from "@/scripts/DmDrawerContext";
import { getOrCreateDm } from "@/lib/dmApi";
import { decodeToken } from "@/lib/tokenUtils";
import { toastError } from "@/lib/toast";

const POSTS_PAGE = 10;

function formatLastActive(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return date.toLocaleString();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d ago`;
  return date.toLocaleDateString();
}

function formatMemberSince(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Standalone member profile: /o/:orgId/members/:userId
 */
export default function MemberProfilePage() {
  const { orgId, userId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();
  const dmSocket = useOptionalDmSocket();
  const dmDrawer = useOptionalDmDrawer();
  const currentUserId = useMemo(() => {
    if (!accessToken || accessToken.split(".").length !== 3) return null;
    return decodeToken(accessToken)?.sub || null;
  }, [accessToken]);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(null);
  const [messaging, setMessaging] = useState(false);

  // Route → workspace only (omit selectedOrgId so switcher isn't fought mid-navigation).
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
    }
  }, [orgId, setSelectedOrgId]);

  useEffect(() => {
    if (!accessToken || !orgId || !userId) return;
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      setProfileError(null);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/profile`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!cancelled) setProfile(res.data || null);
      } catch (err) {
        if (!cancelled) {
          setProfileError(
            err.response?.data?.message || "Could not load member profile."
          );
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, userId]);

  useEffect(() => {
    if (!accessToken || !orgId || !userId) return;
    let cancelled = false;
    (async () => {
      setTeamsLoading(true);
      setTeamsError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/team/memberships/by-user`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { org_id: orgId, user_id: userId },
        });
        if (!cancelled) {
          setTeams(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setTeamsError(
            err.response?.data?.message || "Could not load teams."
          );
        }
      } finally {
        if (!cancelled) setTeamsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, userId]);

  useEffect(() => {
    if (tab !== "posts" || !accessToken || !orgId || !userId) return;
    if (posts.length > 0) return;
    let cancelled = false;
    (async () => {
      setPostsLoading(true);
      setPostsError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/posts/by-user`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            org_id: orgId,
            user_id: userId,
            limit: POSTS_PAGE,
            offset: 0,
          },
        });
        if (!cancelled) {
          setPosts(Array.isArray(res.data?.posts) ? res.data.posts : []);
          setPostsTotal(Number(res.data?.total) || 0);
        }
      } catch (err) {
        if (!cancelled) {
          setPostsError(
            err.response?.data?.message || "Could not load posts."
          );
        }
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, accessToken, orgId, userId, posts.length]);

  const loadMorePosts = async () => {
    if (!accessToken || !orgId || !userId || postsLoading) return;
    setPostsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/posts/by-user`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          org_id: orgId,
          user_id: userId,
          limit: POSTS_PAGE,
          offset: posts.length,
        },
      });
      const next = Array.isArray(res.data?.posts) ? res.data.posts : [];
      setPosts((prev) => [...prev, ...next]);
      setPostsTotal(Number(res.data?.total) || postsTotal);
    } catch (err) {
      setPostsError(err.response?.data?.message || "Could not load posts.");
    } finally {
      setPostsLoading(false);
    }
  };

  const handleMessage = async () => {
    if (!accessToken || !orgId || !userId || !dmDrawer || messaging) return;
    setMessaging(true);
    try {
      const conversation = await getOrCreateDm(accessToken, orgId, userId);
      dmDrawer.openConversation(conversation.conversation_id, {
        peer: conversation.peer || profile,
      });
    } catch (error) {
      toastError(error, "Could not open conversation");
    } finally {
      setMessaging(false);
    }
  };

  const display = profile || {};
  const name = getFullName(display) || "Member";
  const livePresence = dmSocket?.presenceByUserId;
  const presence = resolvePresence(display, livePresence);
  const lastActiveLabel = formatLastActive(display.last_active_at);
  const memberSinceLabel = formatMemberSince(display.joined_at);
  const contactRows = [
    display.org_email ? { label: "Email", value: display.org_email } : null,
    display.phone_number
      ? { label: "Phone", value: display.phone_number }
      : null,
    display.office_location
      ? { label: "Office", value: display.office_location }
      : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6">
      <div className="mb-6 flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <OrgMemberAvatar
            user={display}
            className="h-14 w-14"
            fallbackClassName="text-base font-semibold"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold truncate">{name}</h1>
            {display.title ? (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                {display.title}
              </p>
            ) : null}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {presence ? (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={cn("h-2 w-2 rounded-full shrink-0", presence.dot)}
                    aria-hidden
                  />
                  {presence.label}
                </span>
              ) : null}
              {lastActiveLabel ? (
                <span>Last active {lastActiveLabel}</span>
              ) : null}
            </div>
          </div>
          {dmDrawer && currentUserId !== userId ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={messaging}
              onClick={handleMessage}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              {messaging ? "Opening…" : "Message"}
            </Button>
          ) : null}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="posts">Posts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          {profileLoading && !profile ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : profileError ? (
            <p className="text-sm text-destructive">{profileError}</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <section className="rounded-xl border bg-card p-5 shadow-sm">
                <h2 className="text-sm font-semibold">Member details</h2>
                <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-muted-foreground">Full name</dt>
                    <dd className="mt-1 font-medium">{name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">
                      Workplace title
                    </dt>
                    <dd className="mt-1 font-medium">
                      {display.title || "Not set"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">
                      Member since
                    </dt>
                    <dd className="mt-1 font-medium">
                      {memberSinceLabel || "Not available"}
                    </dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-xl border bg-card p-5 shadow-sm">
                <h2 className="text-sm font-semibold">Workplace contact</h2>
                {contactRows.length === 0 ? (
                  <p className="mt-4 text-sm text-muted-foreground">
                    No contact details shared in this workplace.
                  </p>
                ) : (
                  <dl className="mt-4 space-y-4">
                    {contactRows.map((row) => (
                      <div key={row.label}>
                        <dt className="text-xs text-muted-foreground">
                          {row.label}
                        </dt>
                        <dd className="mt-1 font-medium break-all">
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>

              <section className="rounded-xl border bg-card p-5 shadow-sm md:col-span-2">
                <h2 className="text-sm font-semibold">Teams</h2>
                {teamsLoading ? (
                  <p className="mt-4 text-sm text-muted-foreground">
                    Loading teams…
                  </p>
                ) : teamsError ? (
                  <p className="mt-4 text-sm text-destructive">{teamsError}</p>
                ) : teams.length === 0 ? (
                  <p className="mt-4 text-sm text-muted-foreground">
                    No visible team memberships.
                  </p>
                ) : (
                  <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {teams.map((team) => {
                      const teamId = team.team_id ?? team.teamId;
                      const href = teamReferenceRoute(orgId, teamId);
                      return (
                        <Link
                          key={teamId}
                          to={href || "#"}
                          className="rounded-lg border bg-muted/20 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/60"
                        >
                          <span className="block truncate">
                            {team.name || `Team ${teamId}`}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}
        </TabsContent>

        <TabsContent value="posts" className="mt-0">
          {postsLoading && posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading posts…</p>
          ) : postsError ? (
            <p className="text-sm text-destructive">{postsError}</p>
          ) : posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No posts yet.</p>
          ) : (
            <ul className="space-y-3">
              {posts.map((post) => {
                const postId = post.post_id || post.postId;
                const href = permalinkForPost(orgId, postId);
                const excerpt = plainExcerpt(
                  markdownToPlain(post.content || ""),
                  160
                );
                return (
                  <li key={postId}>
                    <Link
                      to={href}
                      className="block rounded-lg border px-4 py-3 hover:bg-muted/40"
                    >
                      <div className="font-medium text-sm">
                        {post.title || "Untitled"}
                      </div>
                      {excerpt ? (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {excerpt}
                        </p>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
              {posts.length < postsTotal ? (
                <li>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={postsLoading}
                    onClick={loadMorePosts}
                  >
                    {postsLoading ? "Loading…" : "Load more"}
                  </Button>
                </li>
              ) : null}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
